/**
 * @file MineralPricingModal.js
 * @version 4.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-14
 * @modified 2026-10-30
 * @description View / edit / add a mineral product: the inventory item's own
 *   fields, exactly as Edit Item shows them, and the mineral pricing beneath.
 *
 *   4.0.0 (Cam, 2026-10-14): "Can the mineral edit modal also have the fields
 *   in the normal item modal … User wants to be able to set the units and
 *   pallet size etc. for a mineral product but can't." A mineral item (sales
 *   account 206) opens here rather than in Edit Item — from Mineral Pricing and
 *   from Search Inventory — so its units, weight, qty per pallet, accounts and
 *   tax types could not be set anywhere in the system.
 *
 *   ITEM — InventoryItemFields (the Add / Edit Item fields), loaded from the
 *   item itself (GET /api/inventory/:id), with Edit Item's validation, wording
 *   check, Xero conflict check and save request (saveItemRequest: PUT
 *   /api/inventory/:id, pushed to Xero first — sync-or-fail). The old Item
 *   Code / Cost / Description boxes are gone because the item fields hold them:
 *   Cost IS the item's purchase unit price (Purchased → Unit Price) and the
 *   old Description IS the item's sales description (Xero's Description), the
 *   same columns the old PATCH …/mineral-fields wrote.
 *
 *   MINERAL PRICING — haulage, margin (blank = POA, as before) and notes, and
 *   the sale price they give (cost + haulage + margin), saved through the
 *   mineral routes as before.
 *
 *   SAVE — one button. The item first, and only when an item field changed;
 *   then the mineral pricing. A refused item save (Xero included) stops there:
 *   nothing was written on either side, said as Edit Item says it. A mineral
 *   save that fails after the item saved says so plainly — the item is saved,
 *   the mineral pricing is not — and Save again sends only the mineral pricing
 *   (the form is re-seeded from the saved item, so it no longer differs).
 *
 *   COSTS — without `costs.view` the server redacts the purchase price, cost
 *   pool and margin. They read "Hidden" here, and Save is disabled with the
 *   reason: both requests would write the blanks back over the real figures.
 *
 *   4.1.0 (2026-10-30, Cam): a changed item code asks first, as Edit Item does
 *   (ItemCodeChangeDialog — the lines here that take the new code with it).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import Input from './Input';
import SearchableDropdown from './SearchableDropdown';
import AuditHistoryModal from './AuditHistoryModal';
import AlertDialog from './AlertDialog';
import ConflictResolutionModal from './ConflictResolutionModal';
import LoadingBar from './LoadingBar';
import LoadingSpinner from './LoadingSpinner';
import InventoryItemFields, {
  BLANK_ITEM_FORM, formFromItem, validateItemForm, checkItemText, textFieldsToCheck,
  useItemFormLists, ItemTextWarningDialog, pushesItemToXero, changedItemFields,
  checkItemConflict, saveItemRequest, ItemCodeChangeDialog
} from './InventoryItemFields';
import useAccess from '../hooks/useAccess';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import './SearchInventory.css';
import './InventoryItemFormModal.css';
import './MineralPricing.css';

const numOrNull = (v) => (v === null || v === undefined || v === '') ? null : (Number.isFinite(Number(v)) ? Number(v) : null);

export const NO_COSTS_SAVE_REASON =
  'Saving needs the See cost & margin permission: the cost and margin are hidden from you, and saving would clear them.';

/**
 * @param {object?}  mineral  a v_mineral_pricing_detail row (id, inventory_item_id,
 *                            item_code, item_name, haulage, margin, notes …);
 *                            null to add mineral pricing for a product
 * @param {string}   mode     'view' | 'edit'
 * @param {function} onClose
 * @param {function} onSave   ({ message, type }) after everything saved
 */
function MineralPricingModal({ mineral, onClose, onSave, mode: initialMode = 'edit' }) {
  const { can } = useAccess();
  const seeCosts = can('costs.view');

  const [mode, setMode] = useState(initialMode);
  const isView = mode === 'view';
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  // Progress for the save, which may include a push of the item to Xero.
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // The inventory item — loaded from the item, never from the mineral row.
  const [itemId, setItemId] = useState(mineral?.inventory_item_id ? String(mineral.inventory_item_id) : '');
  const [item, setItem] = useState(null);
  const [itemLoading, setItemLoading] = useState(false);
  const [itemLoadError, setItemLoadError] = useState('');
  const [formData, setFormData] = useState(BLANK_ITEM_FORM);
  const [initialFormData, setInitialFormData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const loadSeq = useRef(0);

  // Edit Item's wording check and Xero conflict check.
  const [textWarning, setTextWarning] = useState(null); // { findings, args }
  const textAcceptedRef = useRef(false);
  // The code the person has seen the effect of (ItemCodeChangeDialog).
  const codeConfirmedRef = useRef(null);
  const [codeChangePrompt, setCodeChangePrompt] = useState(null); // { plan, args }
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // The item saved on an earlier press whose mineral save then failed.
  const [itemSavedNote, setItemSavedNote] = useState('');
  const itemSavedRef = useRef(null); // { updatedInXero }

  const [mineralForm, setMineralForm] = useState({
    mineral_id: mineral?.id || null,
    haulage: mineral?.haulage != null ? String(mineral.haulage) : '60',
    margin: mineral?.margin != null ? String(mineral.margin) : '',
    notes: mineral?.notes || ''
  });

  useSlideOutButton([mode, saving, !!item]);

  // Account and tax-type lists once the item is here, so an account with a
  // blank tax type gets its Xero default on the item as loaded (as Edit Item).
  const lists = useItemFormLists(!!item, setFormData);

  const loadItem = useCallback(async (id) => {
    const seq = ++loadSeq.current;
    setItemLoading(true);
    setItemLoadError('');
    try {
      const res = await fetch(`${API_URL}/api/inventory/${id}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.item) throw new Error(data.error || 'Could not load the item');
      if (seq !== loadSeq.current) return;
      const seeded = formFromItem(data.item);
      setItem(data.item);
      setFormData(seeded);
      setInitialFormData({ ...seeded });
      setFormErrors({});
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setItem(null);
      setItemLoadError(err.message);
    } finally {
      if (seq === loadSeq.current) setItemLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mineral?.inventory_item_id) loadItem(mineral.inventory_item_id);
    else loadInventoryItems();
    document.body.classList.add('modal-open');
    const cw = document.querySelector('.content-wrapper');
    if (cw) cw.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
      const c = document.querySelector('.content-wrapper');
      if (c) c.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInventoryItems = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/inventory-items`, { credentials: 'include' });
      const items = await res.json();
      setInventoryItems((items || []).filter(i => i.sales_account_code === '206' && !i.is_archived));
    } catch (e) {
      console.error('Error loading inventory items:', e);
    }
  };

  // Add mode: picking a product loads the item, and any existing mineral row.
  const handleItemChange = async (value) => {
    const picked = inventoryItems.find(i => String(i.id) === value);
    if (!picked) return;
    setItemId(value);
    setItemSavedNote('');
    itemSavedRef.current = null;
    textAcceptedRef.current = false;
    loadItem(picked.id);
    let existing = null;
    try {
      const res = await fetch(`${API_URL}/api/pricing/minerals/by-item/${picked.id}`, { credentials: 'include' });
      if (res.ok) existing = await res.json();
    } catch (e) { /* no existing row — will POST */ }
    setMineralForm(prev => ({
      mineral_id: existing?.id || null,
      haulage: existing?.haulage != null ? String(existing.haulage) : (prev.haulage || '60'),
      margin: existing?.margin != null ? String(existing.margin) : prev.margin,
      notes: existing?.notes || prev.notes
    }));
  };

  const setMineral = (field, v) => setMineralForm(prev => ({ ...prev, [field]: v }));

  // Cost is the item's purchase unit price (Purchased → Unit Price).
  const costN = numOrNull(formData.purchase_unit_price) || 0;
  const haulageN = numOrNull(mineralForm.haulage) || 0;
  const marginN = numOrNull(mineralForm.margin);
  const combinedMargin = haulageN + (marginN || 0);
  const salePrice = marginN != null ? costN + haulageN + marginN : null;

  const itemCode = item?.item_code || formData.item_code || mineral?.item_code || '';
  const saveBlockedReason = !seeCosts ? NO_COSTS_SAVE_REASON : null;

  const failSave = (message) => {
    setProgress(0);
    setProgressMessage('');
    setSaving(false);
    setAlertDialog({ isOpen: true, message, type: 'error' });
  };

  const handleSave = async (resolvedConflicts = null, xeroValues = null) => {
    if (saveBlockedReason) return;
    if (!itemId || !item) {
      setAlertDialog({ isOpen: true, message: 'Please select a product from inventory', type: 'warning' });
      return;
    }
    const errors = validateItemForm(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      setAlertDialog({ isOpen: true, message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    // The item is sent only when one of its fields changed.
    const itemChanged = changedItemFields(formData, initialFormData).size > 0;
    if (itemChanged) {
      if (!textAcceptedRef.current) {
        const findings = await checkItemText(textFieldsToCheck(formData, initialFormData, true));
        if (findings.length) {
          setTextWarning({ findings, args: [resolvedConflicts, xeroValues] });
          return;
        }
      }
      // Has Xero moved underneath us? Asked before anything is written.
      if (pushesItemToXero(item) && !resolvedConflicts) {
        const conflict = await checkItemConflict(item, formData, initialFormData);
        if (conflict) {
          setConflictData(conflict);
          setShowConflictModal(true);
          return;
        }
      }
    }

    setSaving(true);
    setProgress(0);
    setProgressMessage('');

    // 1. The item, through Edit Item's own request.
    if (itemChanged) {
      setProgress(25);
      setProgressMessage(pushesItemToXero(item) ? 'Saving the item and updating Xero...' : 'Saving the item...');
      const result = await saveItemRequest({
        item, formData, resolvedConflicts, xeroValues,
        confirmCodeChange: !!codeConfirmedRef.current && codeConfirmedRef.current === formData.item_code
      });
      // A new code: say what it changes here first; nothing has been saved.
      if (!result.ok && result.codeChange) {
        setProgress(0);
        setProgressMessage('');
        setSaving(false);
        setCodeChangePrompt({ plan: result.codeChange, args: [resolvedConflicts, xeroValues] });
        return;
      }
      if (!result.ok) {
        // Nothing was written on either side; the edits are still here.
        failSave(`${result.message} The mineral pricing has not been saved either.`);
        return;
      }
      // Re-seed from the saved item, so a retry after a failed mineral save
      // sends the mineral pricing alone.
      const seeded = formFromItem(result.item);
      setItem(result.item);
      setFormData(seeded);
      setInitialFormData({ ...seeded });
      textAcceptedRef.current = false;
      itemSavedRef.current = { updatedInXero: result.updatedInXero || !!itemSavedRef.current?.updatedInXero };
      setProgress(60);
      setProgressMessage(result.updatedInXero ? 'Item saved and updated in Xero' : 'Item saved');
    }

    // 2. The mineral pricing (haulage / margin / notes). Priced when a margin is set.
    setProgress(75);
    setProgressMessage('Saving mineral pricing...');
    const payload = {
      inventory_item_id: parseInt(itemId, 10),
      haulage: numOrNull(mineralForm.haulage),
      margin: marginN,
      notes: mineralForm.notes || null,
      is_priced: marginN != null
    };
    try {
      const url = mineralForm.mineral_id
        ? `${API_URL}/api/pricing/minerals/${mineralForm.mineral_id}`
        : `${API_URL}/api/pricing/minerals`;
      const res = await fetch(url, {
        method: mineralForm.mineral_id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed to save mineral pricing');
      if (d?.id && !mineralForm.mineral_id) setMineral('mineral_id', d.id);
    } catch (err) {
      console.error('Error saving mineral pricing:', err);
      if (itemSavedRef.current) {
        const xero = itemSavedRef.current.updatedInXero ? ' and updated in Xero' : '';
        const note = `The item ${itemCode} is saved${xero}, but its mineral pricing (haulage, margin, notes) is not: ${err.message}. Save again to send the mineral pricing.`;
        setItemSavedNote(note);
        failSave(note);
      } else {
        failSave(`The mineral pricing was not saved: ${err.message}`);
      }
      return;
    }

    setProgress(100);
    setProgressMessage('Saved');
    const savedItem = itemSavedRef.current;
    // Hand the outcome to the parent so it can show the alert once this modal
    // has closed.
    onSave({
      message: savedItem
        ? `Item ${itemCode} saved${savedItem.updatedInXero ? ' and updated in Xero' : ''}, and its mineral pricing saved`
        : `Mineral pricing saved for ${itemCode}`,
      type: 'success'
    });
  };

  const handleClose = () => {
    if (saving) return;
    setClosing(true);
    setTimeout(onClose, 200);
  };

  const title = isView ? 'View Mineral Product' : (mineral ? 'Edit Mineral Product' : 'Add Mineral Pricing');
  const productName = item?.item_name || mineral?.item_name || '';

  return createPortal(
    <>
      <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
        <div
          className={`modal-content item-modal modal-lg mineral-modal ${closing ? 'closing' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <div className="header-left">
              <h3>{title}</h3>
            </div>
            <div className="header-actions">
              {itemId && (
                <button type="button" className="btn-icon" onClick={() => setShowHistory(true)} title="View history">
                  <span className="material-symbols-rounded">history</span>
                </button>
              )}
              <button type="button" className="btn-icon close-btn" onClick={handleClose} title="Close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          </div>

          <div className="modal-body">
            <div className="mineral-modal-notices">
              {progress > 0 && <LoadingBar progress={progress} message={progressMessage} />}
              {itemSavedNote && (
                <div className="error-message mineral-modal-note" role="status">
                  <span className="material-symbols-rounded">info</span>{itemSavedNote}
                </div>
              )}
              {!seeCosts && (
                <div className="mineral-modal-note">{NO_COSTS_SAVE_REASON}</div>
              )}
            </div>

            {/* Add mode: the product picker. Edit / view: the product it is. */}
            {!mineral ? (
              <section className="mineral-modal-section">
                <div className="detail-group">
                  <label>Product from Inventory <span className="required">*</span></label>
                  <SearchableDropdown
                    options={inventoryItems.map(i => ({
                      value: String(i.id),
                      badge: i.item_code || ' ',
                      label: seeCosts ? `${i.item_name}  —  Cost: ${formatPrice(i.cost || 0)}` : i.item_name,
                      searchText: `${i.item_code || ''} ${i.item_name}`
                    }))}
                    value={itemId}
                    onChange={handleItemChange}
                    placeholder="Select mineral product from inventory..."
                    disabled={isView || saving}
                  />
                </div>
                {!itemId && <p className="mineral-modal-hint">Choose a product to see its item details and mineral pricing.</p>}
              </section>
            ) : (
              <section className="mineral-modal-section">
                <div className="mineral-modal-product">{productName}</div>
              </section>
            )}

            {itemLoading && <LoadingSpinner />}
            {!itemLoading && itemLoadError && (
              <section className="mineral-modal-section">
                <div className="error-message">
                  <span className="material-symbols-rounded">error</span>
                  {itemLoadError}
                </div>
              </section>
            )}

            {!itemLoading && item && (
              <>
                <section className="mineral-modal-section">
                  <h4 className="mineral-modal-section-title">Item</h4>
                  <p className="mineral-modal-hint">
                    The inventory item, as on Edit Item. Changes are sent to Xero when you save.
                  </p>
                </section>
                <InventoryItemFields
                  formData={formData}
                  setFormData={setFormData}
                  formErrors={formErrors}
                  setFormErrors={setFormErrors}
                  lists={lists}
                  item={item}
                  onTextEdited={() => { textAcceptedRef.current = false; }}
                  disabled={isView || saving}
                  hideCosts={!seeCosts}
                />

                <section className="mineral-modal-section mineral-pricing-fields">
                  <h4 className="mineral-modal-section-title">Mineral pricing</h4>
                  <div className="detail-row mineral-pricing-row">
                    <div className="detail-group">
                      <label>Haulage (£)</label>
                      <Input
                        type="number" step="1"
                        value={mineralForm.haulage}
                        onChange={(e) => setMineral('haulage', e.target.value)}
                        placeholder="e.g. 60"
                        disabled={isView || saving}
                        aria-label="Haulage"
                      />
                    </div>
                    <div className="detail-group">
                      <label>Margin (£)</label>
                      {seeCosts ? (
                        <Input
                          type="number" step="1"
                          value={mineralForm.margin}
                          onChange={(e) => setMineral('margin', e.target.value)}
                          placeholder="e.g. 150 (blank = POA)"
                          disabled={isView || saving}
                          aria-label="Margin"
                        />
                      ) : (
                        <div className="detail-value">Hidden</div>
                      )}
                    </div>
                  </div>
                  <div className="detail-group">
                    <label>Notes</label>
                    <Input
                      type="text"
                      value={mineralForm.notes}
                      onChange={(e) => setMineral('notes', e.target.value)}
                      placeholder="Optional notes (e.g. Price ref AH, POA)"
                      disabled={isView || saving}
                      aria-label="Notes"
                    />
                  </div>

                  {/* Derived: cost (the item's purchase unit price) + haulage + margin. */}
                  {seeCosts && (
                    <div className="mineral-breakdown" aria-label="Calculated sale price">
                      <div className="mineral-breakdown-caption">Calculated (haulage + margin added to cost):</div>
                      <div className="mineral-breakdown-row"><span>Cost (purchase unit price):</span><span>{formatPrice(costN)}</span></div>
                      <div className="mineral-breakdown-row"><span>Haulage:</span><span>{formatCurrency(haulageN)}</span></div>
                      <div className="mineral-breakdown-row"><span>Margin:</span><span>{marginN != null ? formatCurrency(marginN) : '—'}</span></div>
                      <div className="mineral-breakdown-row is-subtotal">
                        <span>Combined margin:</span><span>{formatCurrency(combinedMargin)}</span>
                      </div>
                      <div className="mineral-breakdown-row is-total">
                        <span>Sale Price:</span>
                        <span className={salePrice != null ? 'is-priced' : 'is-poa'} data-testid="mineral-sale-price">
                          {salePrice != null ? formatPrice(salePrice) : 'POA'}
                        </span>
                      </div>
                    </div>
                  )}
                </section>
              </>
            )}
          </div>

          <div className="modal-footer">
            {isView ? (
              <button type="button" className="btn-slideout btn-slideout-primary" onClick={() => setMode('edit')}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
                <span className="btn-label">Edit</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-slideout btn-slideout-primary"
                  onClick={() => handleSave()}
                  disabled={saving || !item || !!saveBlockedReason}
                  title={saveBlockedReason || (!item ? 'Choose a product first' : 'Save the item and its mineral pricing')}
                >
                  <div className="icon-wrapper">
                    <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>{saving ? 'sync' : 'save'}</span>
                  </div>
                  <span className="btn-label">{saving ? 'Saving...' : mineral ? 'Update' : 'Create'}</span>
                </button>
                <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose} disabled={saving}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Cancel</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {textWarning && (
        <ItemTextWarningDialog
          findings={textWarning.findings}
          onConfirm={() => {
            const [rc, xv] = textWarning.args;
            setTextWarning(null);
            textAcceptedRef.current = true;
            handleSave(rc, xv);
          }}
          onCancel={() => setTextWarning(null)}
        />
      )}

      {/* A new code: the lines that take it with them, before saving. */}
      {codeChangePrompt && (
        <ItemCodeChangeDialog
          plan={codeChangePrompt.plan}
          onConfirm={() => {
            const [rc, xv] = codeChangePrompt.args;
            codeConfirmedRef.current = formData.item_code;
            setCodeChangePrompt(null);
            handleSave(rc, xv);
          }}
          onCancel={() => setCodeChangePrompt(null)}
        />
      )}

      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => { setShowConflictModal(false); setConflictData(null); }}
        onResolve={({ resolutions, xeroValues }) => {
          setShowConflictModal(false);
          setConflictData(null);
          handleSave(resolutions, xeroValues);
        }}
        entityType="item"
        conflicts={conflictData?.conflicts || []}
        userChangedFields={conflictData?.userChangedFields || new Set()}
        xeroUpdatedAt={conflictData?.xeroUpdatedAt}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {showHistory && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="inventory_item"
          entityId={parseInt(itemId, 10)}
          entityIdentifier={itemCode}
        />
      )}
    </>,
    document.body
  );
}

export default MineralPricingModal;
